import { Badge, Collapse, Typography } from 'antd'
import {
  useComments,
  useReveal,
} from 'features/libra/comments/CommentsProvider'
import { SignoffPanel } from 'features/libra/signoff/SignoffPanel'
import { getNiiReportConfig } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { NiiPdfExportButton } from 'features/libra/stepComponents/niiReport/pdf/NiiPdfExportButton'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { ActiveStepIdOverride } from 'features/libra/ticket/TicketStateContext'
import { useInactiveStepIds } from 'features/libra/ticket/useInactiveStepIds'
import {
  LibraWorflowStepComponentProps,
  LibraWorkflowStep,
  StepValidationResult,
} from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import styled from 'styled-components'

import { Component as ConditionalFormComponent } from './ConditionalForm'
import { NiiJustificationsPanel } from './niiJustifications/NiiJustificationsPanel'
import { NiiReportReviewPanel } from './niiReview/NiiReportReviewPanel'
import {
  type NiiReviewConfig,
  NiiReviewConfigSchema,
} from './niiReview/niiReviewConfig'
import { ZoneAnalysisReviewPanel } from './niiReview/ZoneAnalysisReviewPanel'
import { Component as NiiScreeningComponent } from './NiiScreening'
import {
  ZONE_ANALYSIS_STEP_COMPONENT,
  zoneAnalysisConfigSchema,
} from './zoneAnalysis/zoneAnalysisApi'

type ReadonlyStepComponent = FC<{
  config: Record<string, unknown>
  name: string
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
  readonly?: boolean
}>

/**
 * Components that support the `readonly` prop natively.
 * These are rendered directly with readonly={true} instead of using a dedicated review panel.
 */
const READONLY_STEP_COMPONENTS: Record<string, ReadonlyStepComponent> = {
  // Safe to cast: step configs extend Record<string, unknown> and are validated by each component
  ConditionalForm: ConditionalFormComponent as ReadonlyStepComponent,
  NiiScreening: NiiScreeningComponent as ReadonlyStepComponent,
}

/**
 * Components that use dedicated readonly review panels (because the step
 * component's editing UI is too deeply coupled to make a simple readonly prop practical).
 */
const REVIEW_PANEL_RENDERERS: Record<
  string,
  FC<{ config: Record<string, unknown> }>
> = {
  ZoneAnalysis: ZoneAnalysisReviewPanel,
  NiiReport: NiiReportReviewPanel,
}

const noopSetValidationResult: Dispatch<
  SetStateAction<StepValidationResult>
> = () => {}

/** Its own collapse, so it cannot collide with a step id in `stepSequence`. */
const SIGNOFF_PANEL_KEY = 'signoff'

const StepReviewPanel: FC<{ step: LibraWorkflowStep; skipped: boolean }> = ({
  step,
  skipped,
}) => {
  if (skipped) {
    return <PanelNotice>Not applicable</PanelNotice>
  }

  const ReadonlyComponent = READONLY_STEP_COMPONENTS[step.component]
  if (ReadonlyComponent) {
    return (
      <ActiveStepIdOverride stepId={step.id}>
        <ReadonlyComponent
          config={step.config}
          name={step.name}
          setValidationResult={noopSetValidationResult}
          readonly
        />
      </ActiveStepIdOverride>
    )
  }

  const ReviewPanel = REVIEW_PANEL_RENDERERS[step.component]
  if (ReviewPanel) {
    return (
      <ActiveStepIdOverride stepId={step.id}>
        <ReviewPanel config={step.config} />
      </ActiveStepIdOverride>
    )
  }

  return <PanelNotice>Review not available for this step type.</PanelNotice>
}

export const Component: FC<LibraWorflowStepComponentProps<NiiReviewConfig>> = ({
  config,
}) => {
  const workflow = useWorkflow()

  const parsedConfig = useMemo(
    () => NiiReviewConfigSchema.parse(config),
    [config]
  )

  // Review step is always valid - nothing to edit
  useReportValidation(undefined)

  const reviewSteps = useMemo(() => {
    return parsedConfig.stepSequence
      .map((stepId) => workflow.steps.find((s) => s.id === stepId))
      .filter((s): s is LibraWorkflowStep => s !== undefined)
  }, [parsedConfig.stepSequence, workflow.steps])

  const { getUnresolvedCountForStep } = useComments()
  const inactiveStepIds = useInactiveStepIds()

  // Following a comment link to a reviewed step opens its section here
  // (the step navigator leaves the user on this page for those steps).
  const [openKeys, setOpenKeys] = useState<string[]>([])
  useReveal((anchor) => {
    if (!parsedConfig.stepSequence.includes(anchor.stepId)) return
    setOpenKeys((keys) =>
      keys.includes(anchor.stepId) ? keys : [...keys, anchor.stepId]
    )
  })

  const { justifications } = parsedConfig
  const { signoffConfig } = workflow

  const zoneFieldName = useMemo(() => {
    const zoneStep = reviewSteps.find(
      (step) => step.component === ZONE_ANALYSIS_STEP_COMPONENT
    )
    return zoneAnalysisConfigSchema.safeParse(zoneStep?.config).data
      ?.zoneFieldName
  }, [reviewSteps])

  const collapseItems = useMemo(() => {
    const items = reviewSteps.map((step) => {
      const unresolvedCount = getUnresolvedCountForStep(step.id)
      return {
        key: step.id,
        label: (
          <PanelLabel>
            {step.name}
            {unresolvedCount > 0 && (
              <Badge
                count={unresolvedCount}
                size='small'
                style={{ marginLeft: 8 }}
              />
            )}
          </PanelLabel>
        ),
        children: (
          <StepReviewPanel step={step} skipped={inactiveStepIds.has(step.id)} />
        ),
      }
    })

    // Appended last: the decision log spans the whole ticket rather than any
    // one of the steps above.
    if (justifications) {
      items.push({
        key: 'justifications',
        label: <PanelLabel>{justifications.title}</PanelLabel>,
        children: (
          <NiiJustificationsPanel
            config={justifications}
            zoneFieldName={zoneFieldName}
          />
        ),
      })
    }

    return items
  }, [
    reviewSteps,
    getUnresolvedCountForStep,
    justifications,
    zoneFieldName,
    inactiveStepIds,
  ])

  const niiReportConfig = useMemo(
    () => getNiiReportConfig(reviewSteps),
    [reviewSteps]
  )

  return (
    <Container>
      <Typography.Text type='secondary'>
        Readonly summary of all steps. Review the collected data before
        completing the workflow.
      </Typography.Text>
      <StyledCollapse
        items={collapseItems}
        bordered={false}
        activeKey={openKeys}
        onChange={setOpenKeys}
      />
      {signoffConfig && (
        <StyledCollapse
          bordered={false}
          defaultActiveKey={SIGNOFF_PANEL_KEY}
          items={[
            {
              key: SIGNOFF_PANEL_KEY,
              label: <PanelLabel>Sign-off</PanelLabel>,
              children: <SignoffPanel config={signoffConfig} />,
            },
          ]}
        />
      )}
      {niiReportConfig && (
        <NiiPdfExportButton
          fields={niiReportConfig.fields}
          zoneFieldName={niiReportConfig.zoneFieldName}
        />
      )}
    </Container>
  )
}

export const useValidateConfig = ({
  config,
}: {
  config: Record<string, unknown>
  name: string
}) => {
  return useMemo(
    () => NiiReviewConfigSchema.safeParse(config).success,
    [config]
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const StyledCollapse = styled(Collapse)`
  .ant-collapse-header {
    padding: 12px 16px !important;
    font-weight: 600;
  }

  .ant-collapse-content-box {
    padding: 12px 16px !important;
  }
`

const PanelLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
`

const PanelNotice = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
  padding: 0.5rem 0;
`
